import { USDC_DECIMALS, type UserTrade } from '@/lib/api/speed';

import type { LivePosition } from '../hooks/useLivePosition';
import type { ActivePosition, ClosedTrade } from '../types';

import { computePnl, exchangePnlPercent } from './pnl';
import { getPriceConfig } from './priceFormat';
import { leverageFromTrade, priceAmount, tokenAmount } from './tradeFormat';

/**
 * Everything a PnL share card renders, normalised so the card itself never
 * has to know which tab opened it. `kind` only decides one label: an open
 * position quotes a MARK PRICE, a closed one quotes the EXIT PRICE it got.
 */
export interface PnlCardData {
    kind: 'unrealized' | 'realized';
    /** Hyperliquid coin, upper-cased for display (e.g. "BTC"). */
    coin: string;
    side: 'LONG' | 'SHORT';
    leverage: number | null;
    pnlValue: number;
    /** Return on margin, as a percentage. Null when margin is unknown. */
    pnlPercent: number | null;
    /** Margin committed, USD. */
    invested: number | null;
    /** Notional position value, USD. */
    positionValue: number | null;
    entryPrice: number | null;
    /** Live mark price while open; realised exit price once closed. */
    markPrice: number | null;
    /** Position size in asset units. */
    sizeToken: number | null;
}

/** The fields of a live position the card actually reads. */
type PositionInput = Pick<
    LivePosition,
    | 'size'
    | 'isLong'
    | 'entryPx'
    | 'unrealizedPnl'
    | 'returnOnEquity'
    | 'marginUsed'
    | 'positionValue'
    | 'leverage'
>;

/**
 * Recovers the mark price the backend valued a position at.
 *
 * `GET /speed/position/:coin` returns a marked PnL but not the price it was
 * marked at, and the price feed only carries the selected market — so a card
 * shared for any other coin has no live price to quote. Inverting the PnL
 * gives it back: pnl = size × (mark − entry) for a long, negated for a short.
 */
export function markPriceFromPosition(position: PositionInput): number | null {
    const { size, entryPx, unrealizedPnl, isLong } = position;
    if (size === null || !size || entryPx === null || unrealizedPnl === null) {
        return null;
    }
    const move = unrealizedPnl / size;
    return isLong ? entryPx + move : entryPx - move;
}

/**
 * Unrealised card for an open position. Pass the live feed's current value as
 * `markPrice` when the card is for the selected market; leave it null and the
 * mark is recovered from the position itself.
 */
export function pnlCardFromPosition(
    coin: string,
    position: PositionInput,
): PnlCardData | null {
    if (position.unrealizedPnl === null || position.isLong === null) {
        return null;
    }
    return {
        kind: 'unrealized',
        coin: coin.toUpperCase(),
        side: position.isLong ? 'LONG' : 'SHORT',
        leverage: position.leverage,
        pnlValue: position.unrealizedPnl,
        // returnOnEquity is a fraction (0.25 = +25%).
        pnlPercent: exchangePnlPercent(position),
        invested: position.marginUsed,
        positionValue: position.positionValue,
        entryPrice: position.entryPx,
        // Derived from the position, NEVER the price feed. Hyperliquid computes
        // unrealizedPnl from MARK — an oracle fair value — while the feed carries
        // LAST, the most recent trade. Labelling last as "Mark price" put a number
        // on the card that its own PnL could not reconcile with (measured $22 out
        // on a live BTC position). clearinghouseState has no per-position markPx,
        // so recovering it from the position is the correct move, and it is
        // self-consistent by construction.
        markPrice: markPriceFromPosition(position),
        sizeToken: position.size,
    };
}

/**
 * Realised card for a closed trade. Returns null for a row that never filled
 * (a Failed trade has no pnl), which is also exactly when the row should not
 * offer a share button.
 */
export function pnlCardFromUserTrade(trade: UserTrade): PnlCardData | null {
    const pnl = priceAmount(trade.pnl);
    if (pnl === null) return null;

    const invested = tokenAmount(trade.margin, USDC_DECIMALS);
    const entryPrice = priceAmount(trade.entryPrice);
    const sizeToken = trade.qty !== null ? priceAmount(trade.qty) : null;
    return {
        kind: 'realized',
        coin: trade.pairBase.toUpperCase(),
        side: trade.isLong ? 'LONG' : 'SHORT',
        leverage: leverageFromTrade(trade, invested, entryPrice),
        pnlValue: pnl,
        pnlPercent:
            invested !== null && invested > 0 ? (pnl / invested) * 100 : null,
        invested,
        positionValue:
            sizeToken !== null && entryPrice !== null
                ? sizeToken * entryPrice
                : null,
        entryPrice,
        markPrice: priceAmount(trade.closePrice),
        sizeToken,
    };
}

/**
 * Unrealised card for a demo position, which exists only in the store — there
 * is no backend position to read, so the same PnL maths the terminal shows
 * (leverage-aware ROI on margin) produces the card.
 */
export function pnlCardFromDemoPosition(
    coin: string,
    position: ActivePosition,
    markPrice: number,
): PnlCardData {
    const { pnlValue, pnlPercent } = computePnl(
        position.entryPrice,
        markPrice,
        position.type,
        position.amount,
        position.leverage,
    );
    const notional = position.amount * position.leverage;
    return {
        kind: 'unrealized',
        coin: coin.toUpperCase(),
        side: position.type,
        leverage: position.leverage,
        pnlValue,
        pnlPercent,
        invested: position.amount,
        positionValue: notional,
        entryPrice: position.entryPrice,
        markPrice,
        sizeToken:
            position.entryPrice > 0 ? notional / position.entryPrice : null,
    };
}

/**
 * Realised card for a demo trade. The demo store already keeps the closed
 * figures, so nothing is recomputed here — only reshaped.
 */
export function pnlCardFromClosedTrade(
    trade: ClosedTrade,
    fallbackCoin: string,
): PnlCardData {
    const notional = trade.amount * trade.leverage;
    return {
        kind: 'realized',
        coin: (trade.coin ?? fallbackCoin).toUpperCase(),
        side: trade.type,
        leverage: trade.leverage,
        pnlValue: trade.pnlValue,
        pnlPercent: trade.pnlPercent,
        invested: trade.amount,
        positionValue: notional,
        entryPrice: trade.entryPrice,
        markPrice: trade.exitPrice,
        sizeToken: trade.entryPrice > 0 ? notional / trade.entryPrice : null,
    };
}

// ── Card-face formatters ────────────────────────────────────────────────
// The card is a fixed-width image, so every figure is abbreviated to fit its
// cell rather than allowed to reflow the layout.

const PLACEHOLDER = '—';

/** "$2.07K" · "$393.41" · "$1.20M" — abbreviated from four figures up. */
export function formatUsdCompact(value: number | null): string {
    if (value === null) return PLACEHOLDER;
    const abs = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(2)}K`;
    return `${sign}$${abs.toFixed(2)}`;
}

/** "$77,610.0" — thousand separators and precision scaled to the magnitude. */
export function formatCardPrice(value: number | null): string {
    if (value === null) return PLACEHOLDER;
    return `$${getPriceConfig(value).formatDisplay(value)}`;
}

/** "0.000640 BTC" — enough decimals to keep a fractional size readable. */
export function formatCardSize(value: number | null, coin: string): string {
    if (value === null) return PLACEHOLDER;
    return `${value.toFixed(value >= 1 ? 4 : 6)} ${coin}`;
}

/** "+$49.68" — sign outside the dollar, never "$-49.68". */
export function formatCardPnl(value: number): string {
    return `${value >= 0 ? '+' : '-'}$${Math.abs(value).toFixed(2)}`;
}

/** "+0.04%" */
export function formatCardPercent(value: number | null): string {
    if (value === null) return PLACEHOLDER;
    return `${value >= 0 ? '+' : '-'}${Math.abs(value).toFixed(2)}%`;
}
